/* eslint-disable no-use-before-define */
import { FC, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Group, Typography } from '@screentone/core';
import { useQueryClient } from '@tanstack/react-query';
import { differenceWith, isEqual } from 'lodash';

import { Accordion } from 'components/accordion/Accordion';
import { useAlert } from 'contexts/alert/useAlert';
import {
  ContentParametersMapping,
  useContentParametersMappingQuery,
  useGetLiveContentForContentParametersQuery,
  useUpdateContentParametersMutation
} from 'data/generated/graphql';
import { useConvertedProperty } from 'hooks';
import { useIsAdmin, useIsSuper } from 'hooks/roles';
import { reportError } from 'utils/errorBoundary';
import {
  AccordionTitleWithUndo,
  ContentParametersError,
  ContentParametersNavigationControls,
  LoadingContentParameters
} from './components';
import { MappingProvider } from './MappingContext';
import { ContentParametersEditor, ContentParametersHistory, MappingViewHeader } from './MappingView';
import { useMappingState } from './useMappingState';
import { getArtifactName, getSortedTreatmentList, isValidRange, validate } from './utils';

const ContentParameterSettings: FC = () => {
  const { allessehId } = useParams();
  const currentProperty = useConvertedProperty();
  const { data, error } = useContentParametersMappingQuery(
    { allessehId: allessehId!, publicationKey: currentProperty! },
    { enabled: !!allessehId && !!currentProperty, staleTime: 60000 }
  );

  const isAdmin = useIsAdmin();
  const isSuper = useIsSuper();
  const canManage = isAdmin || isSuper;

  const {
    data: liveContentData,
    error: liveContentError,
    isFetching: isFetchingLiveContent
  } = useGetLiveContentForContentParametersQuery(
    { allessehId: allessehId!, publicationKey: currentProperty! },
    { enabled: !!allessehId && !!currentProperty, staleTime: 60000 }
  );

  const queryClient = useQueryClient();
  const mutation = useUpdateContentParametersMutation();
  const { alertError, alertSuccess } = useAlert();
  const { mappingState, dispatch } = useMappingState([]);

  const sortedTreatmentList = useMemo(() => getSortedTreatmentList(liveContentData), [liveContentData]);

  useEffect(() => {
    // Sync the state with fetched data: https://tkdodo.eu/blog/breaking-react-querys-api-on-purpose
    if (data) {
      dispatch({
        type: 'SET_MAPPINGS',
        payload: {
          mappings: data.getContentParametersMapping
        }
      });
    }
  }, [data, dispatch]);

  useEffect(() => {
    dispatch({
      type: 'SORT_TREATMENT_TYPES',
      payload: {
        sortedTreatmentList
      }
    });
  }, [dispatch, sortedTreatmentList, data]);

  useEffect(() => {
    if (liveContentError) {
      reportError(
        `Could not fetch live version for artifact ${allessehId}. Treatment types list will remain unordered.`,
        liveContentError as Error
      );
    }
  }, [alertError, allessehId, liveContentError]);

  const allessehIdParts = allessehId?.split('_');
  if (currentProperty && allessehIdParts && allessehIdParts.length > 0) {
    const [, publicationInAllessehId] = allessehIdParts;
    if (publicationInAllessehId && publicationInAllessehId.toLowerCase() !== currentProperty.toLowerCase()) {
      return (
        <>
          <Typography variant="header2">Error</Typography>
          <Typography variant="bodytext">
            The section page ID &quot;{allessehId}&quot; does not belong to the publication &quot;{currentProperty}
            &quot;.
          </Typography>
        </>
      );
    }
  }

  // eslint-disable-next-line unused-imports/no-unused-vars
  const handleSaveChanges = () => {
    const changedMappings = differenceWith(mappingState.modifiedMappings, mappingState.originalMappings, isEqual);

    const errorsArr = changedMappings.map((mapping) => {
      const isValidSchema = validate(mapping.parameters);

      return {
        treatmentType: mapping.treatmentType,
        errors: isValidRange(mapping.parameters),
        schemaErrors: validate.errors,
        isValidSchema
      };
    });

    if (errorsArr.some(({ errors, isValidSchema }) => errors.length > 0 || !isValidSchema)) {
      const errorStrings = errorsArr.map(({ treatmentType, errors, schemaErrors }) => {
        const errorMessage =
          schemaErrors
            ?.map((error) => `${error.instancePath}${error.instancePath ? ': ' : ''}${error.message}`)
            .join('; ') ?? '';

        return `${treatmentType}: ${errors.join('; ')} ${errorMessage}`;
      });

      alertError(`There are errors in the following treatment types: ${errorStrings.join('\n')}`, {
        wrapNewlines: false,
        autoDismiss: false
      });

      return;
    }

    const makeMappingInput = (mapping: ContentParametersMapping) => ({
      treatmentType: mapping.treatmentType,
      comment: mapping.comment,
      parameters: mapping.parameters,
      deprecated: mapping.deprecated
    });

    mutation.mutate(
      {
        publicationKey: currentProperty!,
        contentParametersMappingCreateInput: {
          allessehId: allessehId!,
          mappings: changedMappings.map(makeMappingInput)
        }
      },
      {
        onSuccess: async (data) => {
          if (data.updateParametersMapping.length) {
            const failedTreatments = data.updateParametersMapping.map((m) => m).join(', ');

            alertError(`The following treatments could not be updated: ${failedTreatments}`);

            return Promise.resolve();
          }

          dispatch({
            type: 'CLEAR_HISTORY_INDEXES'
          });

          alertSuccess('Content presentation mapping updated successfully.');

          // After a successful mutation, update the original mappings to the current modified mappings
          return queryClient.invalidateQueries(['ContentParametersMapping']);
        },
        onError: (mutationError) => {
          if (mutationError instanceof Error) {
            alertError(`Error updating content presentation mapping: ${mutationError.message}`);

            return;
          }

          alertError('Error updating content presentation mapping.');
        }
      }
    );
  };

  // data first approach: https://tkdodo.eu/blog/status-checks-in-react-query
  if (data && !isFetchingLiveContent) {
    const changedMappings = differenceWith(mappingState.modifiedMappings, mappingState.originalMappings, isEqual);
    // eslint-disable-next-line unused-imports/no-unused-vars
    const hasChanges = changedMappings.length > 0;

    const artifactName = getArtifactName(liveContentData, allessehId);

    if (data.getContentParametersMapping.length === 0) {
      return (
        <>
          <Typography variant="header2">No Content Presentation Mappings</Typography>
          <Typography variant="bodytext">
            There are no content presentation mappings for the section page ID &quot;
            {artifactName}
            &quot;.
          </Typography>
        </>
      );
    }

    return (
      <>
        <ContentParametersNavigationControls artifactName={artifactName} allessehId={allessehId!} />
        <Group margin={{ bottom: 'lg' }} valign="center" align="space-between">
          <Typography variant="header3">{artifactName}</Typography>
          {canManage && (
            <Button primary disabled={!hasChanges || mutation.isLoading} onClick={handleSaveChanges}>
              {mutation.isLoading ? 'Saving changes...' : 'Save Changes'}
            </Button>
          )}
        </Group>
        <Group margin={{ bottom: 'lg' }}>
          <Typography align="justify">
            This is a read-only view of the content presentation mappings for the treatment types in the{' '}
            <Typography weight="bold" inline>
              {artifactName}
            </Typography>{' '}
            section.
          </Typography>
        </Group>
        <Group fullWidth direction="column">
          {mappingState.modifiedMappings.map((mapping, index) => {
            const isModifiedMapping = changedMappings.some(
              (changedMapping) => changedMapping.treatmentType === mapping.treatmentType
            );

            return (
              <MappingProvider
                key={mapping.treatmentType}
                mapping={mapping}
                originalMapping={mappingState.originalMappings[index]}
                dispatch={dispatch}
                historyIndex={mappingState.historyIndexes[mapping.treatmentType]}
              >
                <Group.Item fullWidth>
                  <Accordion
                    TitleComponent={
                      <AccordionTitleWithUndo
                        mappingIsModified={isModifiedMapping}
                        title={`${mapping.label} - (${mapping.treatmentType})`}
                        onUndo={() => {
                          dispatch({ type: 'UNDO_CHANGES', payload: { treatmentType: mapping.treatmentType } });
                          dispatch({
                            type: 'RESET_HISTORY_INDEX',
                            payload: { treatmentType: mapping.treatmentType }
                          });
                        }}
                      />
                    }
                  >
                    <Group direction="column" gap="mlg">
                      {canManage && <ContentParametersHistory />}
                      <MappingViewHeader />
                      <ContentParametersEditor>
                        <ContentParametersEditor.DeprecatedField viewOnly />
                        <ContentParametersEditor.JSONEditor
                          viewOnly
                          parameters={mapping.parameters}
                          treatmentType={mapping.treatmentType}
                          dispatch={dispatch}
                        />
                        <ContentParametersEditor.CommentField viewOnly />
                      </ContentParametersEditor>
                    </Group>
                  </Accordion>
                </Group.Item>
              </MappingProvider>
            );
          })}
        </Group>
      </>
    );
  }

  if (error) {
    <ContentParametersError error={error} />;
  }

  return <LoadingContentParameters>Loading content presentation mapping...</LoadingContentParameters>;
};

export default ContentParameterSettings;
